import { sceneKey, type SceneKey, type SceneRef } from './scene-key';
import type { SaveOutcome } from './save-queue';
import type { Geometry, LayoutObject } from './types';

export interface LocalReconciliation {
  readonly local: ReadonlyMap<string, Geometry>;
  readonly conflicts: ReadonlyMap<string, Geometry>;
}

/**
 * 当前场景的本地乐观覆盖层（`local`/`conflicts`）如何随一批保存结果变化。
 * 只在"这批结果属于当前正在看的场景"时才应该调用——离开场景之后收到的结果
 * 跟这两个 state 已经无关（它们在场景切换时会被整体重置），那种情况要用
 * 下面的 `updateSceneErrors` 记录，而不是碰这两个 Map。
 *
 * **M3B 独立复审 H-2**：旧实现在整批请求失败（`outcome.ok === false`，比如
 * 租约被别人抢走时的 409）时直接 `return`，从不清理 `local` 里被拒绝的乐观
 * 覆盖——画布因此会一直画着一个从未真正保存成功的位置，直到用户切换场景。
 * 这里对齐"逐项拒绝"早就有的处理方式：不管是整批失败还是逐项拒绝，被拒绝的
 * `locationId` 都要从 `local` 里删掉，让 `geometryOf()` 回落到服务端权威几何
 * （配合调用方对相应 query 的 invalidate，见 layout-editor-page.tsx）。
 */
export function reconcileLocalOverrides(
  local: ReadonlyMap<string, Geometry>,
  conflicts: ReadonlyMap<string, Geometry>,
  outcome: SaveOutcome,
): LocalReconciliation {
  if (!outcome.ok) {
    const nextLocal = new Map(local);
    for (const id of outcome.locationIds) nextLocal.delete(id);
    return { local: nextLocal, conflicts };
  }
  const nextLocal = new Map(local);
  const nextConflicts = new Map(conflicts);
  for (const r of outcome.results) {
    if (r.ok) {
      const current = nextLocal.get(r.locationId);
      if (current) nextLocal.set(r.locationId, { ...current, version: r.version });
      nextConflicts.delete(r.locationId);
    } else if (r.code === 'COMMON.VERSION_CONFLICT') {
      // 版本冲突有自己专门的黄框 UI + "以服务端为准/用我的覆盖"，不当成
      // "保存失败"处理——本地覆盖先保留，等用户选择后再解决。
      if (r.current) nextConflicts.set(r.locationId, r.current as Geometry);
    } else {
      // 其它任何硬拒绝（场景不匹配/已归档/被锁定/校验失败……）：回落权威几何。
      nextLocal.delete(r.locationId);
      nextConflicts.delete(r.locationId);
    }
  }
  return { local: nextLocal, conflicts: nextConflicts };
}

/**
 * M3B 独立复审第四轮 NEW-A：**权威场景数据每次落地时，清掉已被服务端超越的
 * 本地乐观覆盖**。
 *
 * 根因：保存成功时 `reconcileLocalOverrides` **保留**该对象的覆盖（只把
 * version 推进到服务端返回值）——这是必要的：settle 那一刻 query 缓存里的
 * 几何还是保存前的旧值，立即删除覆盖会让画布闪回旧位置，直到 invalidate
 * 触发的重新拉取回来。但旧实现**从来没有第二步**：覆盖只会在场景切换或
 * 保存被拒绝时消失，于是"另一个标签页/租约接管者后来改了这个对象"的场景下，
 * 重新拉取带回了更新的权威几何，`geometryOf()` 却永远显示着当年那份已经
 * 过时的本地覆盖。
 *
 * 这里补上第二步：每当权威场景数据（重新拉取）落地，凡是服务端版本**严格
 * 大于**本地覆盖版本的对象，覆盖一律清除，让权威几何显示出来。
 *
 * 两个刻意的边界：
 * - **严格大于，不是 ≥**：版本相等意味着这份覆盖正是产生该服务端状态的那次
 *   保存（值完全一致，遮挡不可见、无害），而且用户可能已经在它之上又做了
 *   还没保存的新编辑（applyGeometry 会把覆盖 version 保持为账本值 = 已保存
 *   版本）——按 ≥ 清除会把这些待保存的编辑从画布上抹掉，回退 pending-save
 *   体验。被真正更新的服务端版本必然严格更大，NEW-A 场景完整覆盖。
 * - **跳过 `conflicts` 里的对象**：版本冲突时服务端必然严格更新，settle
 *   触发的重新拉取如果把"我的"覆盖清掉，冲突横幅上的"用我的覆盖"就没有
 *   东西可提交了——冲突有自己的解决流程（横幅两个按钮），不归这里管。
 *
 * 不在场景数据里的覆盖（对象刚被归档/移走）原样保留：`geometryOf()` 只会
 * 对场景里存在的对象求值，这些条目无害，且场景切换时会整体清空。
 */
export function evictSupersededOverrides(
  local: ReadonlyMap<string, Geometry>,
  conflicts: ReadonlyMap<string, Geometry>,
  objects: readonly LayoutObject[],
): ReadonlyMap<string, Geometry> {
  if (local.size === 0) return local;
  const serverVersions = new Map(objects.map((o) => [o.locationId, o.geometry.version]));
  let next: Map<string, Geometry> | null = null;
  for (const [id, mine] of local) {
    if (conflicts.has(id)) continue;
    const server = serverVersions.get(id);
    if (server !== undefined && server > mine.version) {
      next ??= new Map(local);
      next.delete(id);
    }
  }
  // 没有任何变化时返回原引用：调用方在 effect 里 setLocal，同引用可以让
  // React 直接跳过重渲染，不会造成 render 循环。
  return next ?? local;
}

/**
 * 场景级"还有没有解决的保存失败"跟踪表：key 是 `sceneKey(scene)`（见
 * `scene-key.ts`），value 是那个场景里、最近一次保存尝试硬失败（不含版本
 * 冲突）的 locationId 集合。**这份记录不会因为切换场景（甚至切换仓库）就被
 * 清空**——切场景/切仓库只重置 `local`/`conflicts`（那两个是"当前场景"
 * 概念），这份表则要活过场景切换，直到对应的 locationId later 被成功保存
 * 一次才会被移出。
 *
 * **M3B 独立复审 M-1**：旧实现在 `resultParentId !== 当前场景` 时直接
 * `return`，离开场景之后才落地的失败结果就彻底消失了——`layout-editor-page.
 * tsx` 用这张表分别驱动"当前场景的保存状态指示"（不会被别的场景的成功保存
 * 误清空，这也顺带修了 H-2 报告里"状态显示已全部保存但画布还留着被拒绝的
 * 几何"的那部分）和"有其它场景存在未解决失败"的常驻提示条。
 *
 * **M3B 独立复审第二轮 NEW-2**：key 从"只按 parentId"改成"按
 * `sceneKey({warehouseId, parentId})`"——否则两个不同仓库的根场景
 * （`parentId` 都是 `null`）会共用同一个桶，在仓库 A 里保存失败的记录会在
 * 切到仓库 B 之后被误当成"当前场景（B 的根场景）有未解决失败"显示出来。
 */
export function updateSceneErrors(
  prev: ReadonlyMap<SceneKey, ReadonlySet<string>>,
  scene: SceneRef,
  outcome: SaveOutcome,
): ReadonlyMap<SceneKey, ReadonlySet<string>> {
  const key = sceneKey(scene);
  const next = new Map(prev);
  const updated = new Set(next.get(key));
  if (!outcome.ok) {
    for (const id of outcome.locationIds) updated.add(id);
  } else {
    for (const r of outcome.results) {
      if (r.ok || r.code === 'COMMON.VERSION_CONFLICT') updated.delete(r.locationId);
      else updated.add(r.locationId);
    }
  }
  if (updated.size === 0) next.delete(key);
  else next.set(key, updated);
  return next;
}
